import * as React from 'react';
import Upload from './Upload/Upload';
import { AttachStatus } from '../containers/Request/RequestForm';
import { formUi } from '../constants/globalcss';

/**
 * Props for the FileUploadSection component
 */
export interface IFileUploadSectionProps {
  /** Whether the request type supports file upload (folder_path exists) */
  hasUploadSupport: boolean;
  /** Current attachment status (required, optional, not allowed) */
  attachmentStatus: AttachStatus;
  /** Array of currently selected files */
  selectedFiles: File[];
  /** Function to handle file selection */
  onFileChange: (files: File[]) => void;
  /** Function to handle file removal */
  onRemoveFile: (index: number) => void;
  /** Function to clear all files */
  onClearFiles: () => void;
  /** Upload error text to display */
  uploadErrorText: string;
  /** Same max width as `FormLayout` so the drop zone lines up with fields */
  formContentMaxWidth?: number;
}

/**
 * FileUploadSection component that handles the file upload UI
 * Conditionally renders the upload component based on configuration
 *
 * @param props - Component props
 * @returns JSX element containing the file upload section or null
 */
export const FileUploadSection: React.FC<IFileUploadSectionProps> = React.memo(
  ({
    hasUploadSupport,
    attachmentStatus,
    selectedFiles,
    onFileChange,
    onRemoveFile,
    onClearFiles,
    uploadErrorText,
    formContentMaxWidth = formUi.spacing.formColumnListMax,
  }) => {
    /**
     * Memoized upload component to prevent unnecessary re-renders
     */
    const uploadComponent = React.useMemo(
      () => (
        <Upload
          selectedFiles={selectedFiles}
          onFileChange={onFileChange}
          onRemoveFile={onRemoveFile}
          onClearFiles={onClearFiles}
          attachmentStatus={attachmentStatus}
          errorText={uploadErrorText}
        />
      ),
      [
        selectedFiles,
        onFileChange,
        onRemoveFile,
        onClearFiles,
        attachmentStatus,
        uploadErrorText,
      ]
    );

    // Don't render if upload is not supported or not allowed
    if (!hasUploadSupport || attachmentStatus === AttachStatus.NotAllowed) {
      return null;
    }

    return (
      <div
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          marginLeft: 'auto',
          marginRight: 'auto',
          maxWidth: formContentMaxWidth,
          width: '100%',
        }}
      >
        {uploadComponent}
      </div>
    );
  }
);

FileUploadSection.displayName = 'FileUploadSection';
